/**
 * The events row's behavior half: reads the upcoming events, keeps them by
 * date, holds which view of four is in place, and opens an event or the
 * events page. With the mine option, marks each event a signed-in client has
 * booked (web-studio-sites 6.6) and opens that booking. Draws nothing; the
 * drawing scrolls the track to `firstInView` and reports a swipe back through
 * `scrolledTo`.
 */
import { useEffect, useMemo, useState } from 'react';

import { useSiteEvents } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { getClientTimezone } from '../../format';
import { usePageMover } from '../../pageMover';
import { contract, type EventsRowActions, type EventsRowEvents, type EventsRowState } from './contract';
import { useEventMarks } from './useEventMarks';

export type EventsRowRuntime = PieceRuntime<typeof contract, EventsRowState, EventsRowActions>;

/** Events in one view at desktop width (Gabriel 2026-10-19: four in view, the arrows past four). */
const PER_VIEW = 4;
const ALL_EVENTS_PATH = '/events';

export function useEventsRow(input: PieceInput<EventsRowEvents> = {}): EventsRowRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const navigate = usePageMover();
  const query = useSiteEvents();
  const timezone = useMemo(() => getClientTimezone(), []);
  const [wantedPage, setPage] = useState(0);

  const events = useMemo(
    () =>
      (query.data?.pages ?? [])
        .flatMap((p) => p.events)
        .sort((a, b) => Date.parse(a.startTime) - Date.parse(b.startTime)),
    [query.data],
  );

  const pageCount = Math.max(1, Math.ceil(events.length / PER_VIEW));
  const page = Math.min(Math.max(0, wantedPage), pageCount - 1);
  // The last view ends on the last event, as a scroll clamped at its end does.
  const firstInView = Math.min(page * PER_VIEW, Math.max(0, events.length - PER_VIEW));
  // The API sends events a page at a time: fetch the next read while the view
  // nears the end of what is loaded, and keep Next while one remains.
  const moreToLoad = Boolean(query.hasNextPage);
  const hasMore = events.length > PER_VIEW || moreToLoad;
  const { fetchNextPage, isFetchingNextPage } = query;
  useEffect(() => {
    if (moreToLoad && !isFetchingNextPage && firstInView + 2 * PER_VIEW > events.length) void fetchNextPage();
  }, [moreToLoad, isFetchingNextPage, firstInView, events.length, fetchNextPage]);
  const marks = useEventMarks(events, options.mine, timezone);
  const status: EventsRowState['status'] = query.isError ? 'error' : query.isPending ? 'loading' : 'ready';

  const actions = useMemo<EventsRowActions>(() => {
    const goTo = (to: number) => {
      const next = Math.min(Math.max(0, Math.round(to)), pageCount - (moreToLoad ? 0 : 1));
      if (next === page) return;
      setPage(next);
      emit('paged', { page: next });
    };
    return {
      next: () => goTo(page + 1),
      previous: () => goTo(page - 1),
      scrolledTo: (to) => goTo(to),
      open: (eventId) => {
        const event = events.find((e) => e.id === eventId);
        if (!event) return;
        emit('opened', { eventId });
        navigate(event.path);
      },
      seeAll: () => navigate(ALL_EVENTS_PATH),
      openBooking: (eventId) => {
        const mark = marks[eventId];
        if (mark) navigate(mark.action.href);
      },
    };
  }, [page, pageCount, moreToLoad, events, marks, emit, navigate]);

  const state: EventsRowState = {
    status,
    events,
    perView: PER_VIEW,
    page,
    pageCount,
    firstInView,
    inView: events.slice(firstInView, firstInView + PER_VIEW),
    hasMore,
    canPrevious: hasMore && page > 0,
    canNext: hasMore && (page < pageCount - 1 || moreToLoad),
    timezone,
    allEventsPath: ALL_EVENTS_PATH,
    marks,
  };

  return { state, actions, options };
}
